import { useRuntimeVariables } from 'app-kit'
import { useKnowledgeDocumentDataContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentDataContext'
import { documentMetadataUserEditByEntryId } from 'features/knowledgeDocumentViewer/utils/documentEntryLookups'
import { documentMetadataUserEditEntryId } from 'features/knowledgeDocumentViewer/utils/metadataUserEditIds'
import { useCallback } from 'react'
import { useSWRConfig } from 'swr'
import { RolodexEntryType } from 'types'

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function loadOptionsIncludeDocumentMetadataUserEdit(bodyData: string): boolean {
  try {
    const parsed: unknown = JSON.parse(bodyData)
    if (!isRecord(parsed)) return false
    const types = parsed.type
    if (typeof types === 'string') {
      return types === RolodexEntryType.DOCUMENT_METADATA_USER_EDIT
    }
    if (!Array.isArray(types)) return false
    return types.includes(RolodexEntryType.DOCUMENT_METADATA_USER_EDIT)
  } catch {
    return false
  }
}

export type InvalidateDocumentMetadataUserEditLoads = (
  /** Optional inactive sibling document ids to bust explicitly. */
  bqDocumentIds?: readonly string[]
) => Promise<void>

/** Revalidate document user-edit SWR keys (active + optional sibling ids). */
export function useInvalidateDocumentMetadataUserEditLoads(): InvalidateDocumentMetadataUserEditLoads {
  const { mutate } = useSWRConfig()
  const { ROLODEX_URL } = useRuntimeVariables()
  const { siteID } = useKnowledgeDocumentDataContext()
  const endpoint = `${ROLODEX_URL}/api/v2/entries/load`

  return useCallback(
    async (bqDocumentIds?: readonly string[]) => {
      if (siteID && bqDocumentIds !== undefined && bqDocumentIds.length > 0) {
        await Promise.all(
          bqDocumentIds.map((bqDocumentId) => {
            const entryId = documentMetadataUserEditEntryId(
              siteID,
              bqDocumentId
            )
            const loadOptions = documentMetadataUserEditByEntryId(entryId, {
              site: siteID,
            })
            return mutate([endpoint, JSON.stringify(loadOptions)], undefined, {
              revalidate: true,
            })
          })
        )
      }

      await mutate(
        (key) => {
          if (!Array.isArray(key) || key.length < 2) return false
          if (key[0] !== endpoint) return false
          if (typeof key[1] !== 'string') return false
          return loadOptionsIncludeDocumentMetadataUserEdit(key[1])
        },
        undefined,
        { revalidate: true }
      )
    },
    [endpoint, mutate, siteID]
  )
}
